import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Icon } from "@/components/ui/icon";
import { ImageCropper } from "./ImageCropper";

interface MultiImageUploadProps {
  projectId: string;
  maxImages?: number;
}

interface ProjectImage {
  id: string;
  image_url: string;
  display_order: number;
  caption?: string;
  alt_text?: string;
}

export const MultiImageUpload = ({ projectId, maxImages = 5 }: MultiImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingImage, setEditingImage] = useState<string | null>(null);
  const [cropImage, setCropImage] = useState<{ file: File; url: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchImages = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("project_images")
        .select("*")
        .eq("project_id", projectId)
        .order("display_order");

      if (error) throw error;
      setImages(data || []);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchImages();
    }
  }, [projectId, fetchImages]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (images.length + files.length > maxImages) {
      toast.error(`You can only upload up to ${maxImages} images per project`);
      return;
    }

    const file = files[0];
    const url = URL.createObjectURL(file);
    setCropImage({ file, url });
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    if (!cropImage) return;

    try {
      setUploading(true);
      setCropImage(null);

      const fileExt = cropImage.file.name.split(".").pop() || "jpg";
      const fileName = `${projectId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("project-images")
        .upload(fileName, croppedBlob);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("project-images")
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase
        .from("project_images")
        .insert([{
          project_id: projectId,
          image_url: publicUrl,
          display_order: images.length,
          alt_text: cropImage.file.name.replace(/\.[^/.]+$/, ""),
        }]);

      if (insertError) throw insertError;

      await fetchImages();
      toast.success("Image uploaded successfully");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => {
    if (cropImage) {
      URL.revokeObjectURL(cropImage.url);
    }
    setCropImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = async (imageId: string, imageUrl: string) => {
    try {
      const urlParts = imageUrl.split("/project-images/");
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        await supabase.storage.from("project-images").remove([filePath]);
      }

      const { error } = await supabase
        .from("project_images")
        .delete()
        .eq("id", imageId);

      if (error) throw error;

      await fetchImages();
      toast.success("Image removed");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "An error occurred");
    }
  };

  const moveImage = async (imageId: string, direction: "up" | "down") => {
    const currentIndex = images.findIndex(img => img.id === imageId);
    if (currentIndex === -1) return;

    const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (swapIndex < 0 || swapIndex >= images.length) return;

    try {
      const updates = [
        supabase
          .from("project_images")
          .update({ display_order: swapIndex })
          .eq("id", images[currentIndex].id),
        supabase
          .from("project_images")
          .update({ display_order: currentIndex })
          .eq("id", images[swapIndex].id),
      ];

      await Promise.all(updates);
      await fetchImages();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "An error occurred");
    }
  };

  const updateImageMetadata = async (imageId: string, field: "alt_text" | "caption", value: string) => {
    try {
      const { error } = await supabase
        .from("project_images")
        .update({ [field]: value })
        .eq("id", imageId);

      if (error) throw error;

      setImages(prev =>
        prev.map(img =>
          img.id === imageId ? { ...img, [field]: value } : img
        )
      );
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "An error occurred");
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading images...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Project Images ({images.length}/{maxImages})</Label>
        <Input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading || images.length >= maxImages}
          className="cursor-pointer"
        />
        <p className="text-xs text-muted-foreground">
          Upload up to {maxImages} images. First image will be the cover. Images will be cropped to 16:9.
        </p>
      </div>

      {images.length > 0 && (
        <div className="space-y-4">
          {images.map((image, index) => (
            <div
              key={image.id}
              className="border border-border rounded-lg overflow-hidden"
            >
              <div className="flex gap-4 p-4">
                <div className="relative flex-shrink-0 w-40 h-24 rounded-md overflow-hidden">
                  <img
                    src={image.image_url}
                    alt={image.alt_text || `Project image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {index === 0 && (
                    <div className="absolute top-1 left-1 bg-primary text-primary-foreground px-2 py-0.5 text-xs rounded-full font-medium">
                      Cover
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor={`alt-${image.id}`} className="text-xs">
                      Alt Text (for accessibility & SEO)
                    </Label>
                    <Input
                      id={`alt-${image.id}`}
                      value={image.alt_text || ""}
                      onChange={(e) => updateImageMetadata(image.id, "alt_text", e.target.value)}
                      placeholder="Describe this image..."
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`caption-${image.id}`} className="text-xs">
                      Caption (optional)
                    </Label>
                    <Input
                      id={`caption-${image.id}`}
                      value={image.caption || ""}
                      onChange={(e) => updateImageMetadata(image.id, "caption", e.target.value)}
                      placeholder="Add a caption..."
                      className="h-8 text-sm"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => moveImage(image.id, "up")}
                    disabled={index === 0}
                    className="h-8 w-8"
                  >
                    <Icon name="arrow_upward" size={16} />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => moveImage(image.id, "down")}
                    disabled={index === images.length - 1}
                    className="h-8 w-8"
                  >
                    <Icon name="arrow_downward" size={16} />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemove(image.id, image.image_url)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Icon name="delete" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploading && <p className="text-sm text-muted-foreground">Uploading image...</p>}

      {cropImage && (
        <ImageCropper
          imageSrc={cropImage.url}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={16 / 9}
        />
      )}
    </div>
  );
};
